import React, { useEffect, useState } from "react";
import { Modal, ModalHeader, ModalBody } from "reactstrap";
import { Card } from "@mui/material";
import dayjs from "dayjs";
import ListingModalTable from "./ListingModalTable";
import { useTranslation } from "context/TranslationProvider";

const UserListingsModal = ({
  isOpen,
  toggle,
  listings = [],
  isLoading,
  type,
}) => {
  const { t } = useTranslation();

  const [searchLists, setSearchLists] = useState([]);
  const [startDate, setStartDate] = useState();
  const [searchFrom, setSearchFrom] = useState("");
  const [searchTo, setSearchTo] = useState("");
  const [selectedFrom, setSelectedFrom] = useState({});
  const [selectedTo, setSelectedTo] = useState({});
  const [filteredCities, setFilteredCities] = useState([]);
  const [rotate, setRotate] = useState(false);
  const [noCityMatchMessage, setNoCityMatchMessage] = useState("");

  const today = dayjs();

  const isSelectedFrom = !!selectedFrom?.id;
  const isSelectedTo = !!selectedTo?.id;

  useEffect(() => {
    setSearchLists(listings);
  }, [listings]);

  const calculateMatchScore = (cityName, searchTerm) => {
    let score = 0;
    if (cityName === searchTerm) score += 100;
    if (cityName.startsWith(searchTerm)) score += 50;
    if (cityName.includes(searchTerm)) score += 25;
    return score;
  };

  const filterItems = (term, placeType) => {
    const placeKey = placeType === "from" ? "fromPlace" : "toPlace";
    let baseLists = listings;

    if (placeType === "to" && selectedFrom?.id) {
      baseLists = listings.filter(
        (l) => l.fromPlace?.id === selectedFrom.id
      );
    }

    const cities = baseLists.map((l) => l[placeKey]).filter(Boolean);
    const uniqueCities = Array.from(
      new Map(cities.map((c) => [c.id, c])).values()
    );

    const filtered = uniqueCities.filter((city) =>
      city.name?.toLowerCase().includes(term.toLowerCase())
    );

    const sorted = filtered.sort(
      (a, b) =>
        calculateMatchScore(b.name.toLowerCase(), term.toLowerCase()) -
        calculateMatchScore(a.name.toLowerCase(), term.toLowerCase())
    );

    setFilteredCities(sorted);

    if (!sorted.length) {
      const template =
        placeType === "from"
          ? t("page.allListings.noFromMatch", "No listings found from \"{city}\"")
          : t("page.allListings.noToMatch", "No listings found to \"{city}\"");
      setNoCityMatchMessage(template.replace("{city}", term));
    } else {
      setNoCityMatchMessage("");
    }
  };

  const handleFromChange = (e) => {
    const val = e.target.value;
    setSearchFrom(val);

    if (!val) {
      setSelectedFrom({});
      setFilteredCities([]);
      setNoCityMatchMessage("");
      return;
    }

    filterItems(val, "from");
  };

  const handleToChange = (e) => {
    const val = e.target.value;
    setSearchTo(val);

    if (!val) {
      setSelectedTo({});
      setFilteredCities([]);
      setNoCityMatchMessage("");
      return;
    }

    filterItems(val, "to");
  };

  const handleFromCity = (city) => {
    setSelectedFrom(city);
    setSearchFrom(city.name);
    setFilteredCities([]);
    setNoCityMatchMessage("");
  };

  const handleToCity = (city) => {
    setSelectedTo(city);
    setSearchTo(city.name);
    setFilteredCities([]);
    setNoCityMatchMessage("");
  };

  const toggleFromTo = () => {
    setRotate((p) => !p);
    setSelectedFrom(selectedTo || {});
    setSelectedTo(selectedFrom || {});
    setSearchFrom(selectedTo?.name || "");
    setSearchTo(selectedFrom?.name || "");
  };

  const getSearchSenderListings = () => {
    const formattedDate = startDate
      ? dayjs(startDate).format("DD/MM/YYYY")
      : null;

    const filtered = listings.filter((l) => {
      const fromOk =
        !selectedFrom.name || selectedFrom.name === l.fromPlace?.name;
      const toOk =
        !selectedTo.name || selectedTo.name === l.toPlace?.name;
      const dateOk = formattedDate
        ? formattedDate === dayjs(l.date).format("DD/MM/YYYY")
        : true;

      return fromOk && toOk && dateOk;
    });

    setSearchLists(filtered);
    return filtered;
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        toggle={toggle}
        size="xl"
        className="modal-dialog-centered"
      >
        <ModalHeader toggle={toggle}>
          {type === "sender"
            ? t("page.userListings.senderTitle", "Sender Listings")
            : t("page.userListings.travelerTitle", "Traveler Listings")}
        </ModalHeader>

        <ModalBody className="mt--3">
          <Card elevation={0} className="border">
            <ListingModalTable
              items={searchLists}
              isItemsLoading={isLoading}
              rotate={rotate}
              type={type}
              searchFrom={searchFrom}
              searchTo={searchTo}
              isSelectedFrom={isSelectedFrom}
              isSelectedTo={isSelectedTo}
              filteredCities={filteredCities}
              handleFromChange={handleFromChange}
              handleToChange={handleToChange}
              handleFromCity={handleFromCity}
              handleToCity={handleToCity}
              toggleFromTo={toggleFromTo}
              startDate={startDate}
              setStartDate={setStartDate}
              today={today}
              getSearchSenderListings={getSearchSenderListings}
              noCityMatchMessage={noCityMatchMessage}
            />
          </Card>
        </ModalBody>
      </Modal>
    </>
  );
};

export default UserListingsModal;
